import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Spinner,
  Center,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { KPIConfiguration } from 'src/components/molecules/KPIConfiguration';
import { useModelForm } from '../../hooks/useModelForm';
import { MediaSelectList } from '../molecules/MediaSelectList';
import { CampaignSelectList } from '../molecules/CampaignSelectList';

type EditModelPageProps = {
  modelId: string;
  onComplete: () => void;
};

export const EditModelPage: React.FC<EditModelPageProps> = ({
  modelId,
  onComplete,
}) => {
  const {
    models,
    kpiSources,
    kpiMetrics,
    kpiCategoryOptions,
    mediaPlatforms,
    campaignsWithGroups,
    isLoading,
    isSubmitting,
    areAllModelsValid,
    toastState,
    hideToast,
    handleModelNameChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleCategoryChange,
    handleCategoryReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleSubmit,
    handleCancel,
  } = useModelForm({ mode: 'edit', modelId, onComplete });

  const model = models[0];

  if (isLoading && !model?.modelName) {
    return (
      <Center py={20}>
        <Spinner color="icon.green" size="lg" />
      </Center>
    );
  }

  if (!model) {
    return (
      <Center py={20}>
        <Text color="gray.500">Model not found</Text>
      </Center>
    );
  }

  return (
    <>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
      <Box
        bg="surface.card"
        borderRadius="16px"
        border="1px solid"
        borderColor="border.layout"
        w="100%"
        p={6}>
        {/* Page Title */}
        <Text fontSize="16px" fontWeight={600} color="text.title" mb={6}>
          EDIT YOUR MODEL
        </Text>

        <VStack spacing={6} align="stretch">
          {/* Model Name */}
          <Box>
            <FormControl>
              <FormLabel
                fontSize="16px"
                fontWeight={500}
                color="text.title"
                mb={3}>
                Model name
              </FormLabel>
              <InputGroup>
                <Input
                  placeholder="Name your model"
                  value={model.modelName}
                  onChange={(e) =>
                    handleModelNameChange(model.id, e.target.value)
                  }
                  bg="surface.card"
                  border="1px solid"
                  borderColor="border.layout"
                  borderRadius="md"
                  py={3}
                  px={4}
                  pr={10}
                  fontSize="16px"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                />
                <InputRightElement h="100%">
                  <Icon iconName="pencil" boxSize={4} color="gray.400" />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </Box>

          <KPIConfiguration
            model={model}
            onSourceChange={handleSourceChange}
            onSourceReset={handleSourceReset}
            onMetricChange={handleMetricChange}
            onMetricReset={handleMetricReset}
            onCategoryChange={handleCategoryChange}
            onCategoryReset={handleCategoryReset}
            kpiSources={kpiSources}
            kpiMetrics={kpiMetrics}
            kpiCategories={kpiCategoryOptions}
            isLoading={isLoading}
          />

          <MediaSelectList
            mediaPlatforms={mediaPlatforms}
            selectedMedia={model.selectedMedia}
            onChange={(selectedMedia) =>
              handleMediaChange(model.id, selectedMedia)
            }
            isDisabled={isLoading}
          />

          <CampaignSelectList
            campaigns={campaignsWithGroups}
            selectedCampaigns={model.campaignSelections}
            onChange={(selections) =>
              handleCampaignSelectionsChange(model.id, selections)
            }
            isDisabled={isLoading || model.selectedMedia.length === 0}
            emptyMessage={
              model.selectedMedia.length === 0
                ? 'Select media above to load campaigns'
                : 'No campaigns available'
            }
          />
        </VStack>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify={{ base: 'stretch', md: 'flex-end' }}
          alignItems="center"
          gap={4}
          mt={8}>
          <Button
            buttonName="back-edit-model"
            variant="outline"
            borderColor="border.box"
            color="text.title"
            bg="surface.card"
            borderRadius="lg"
            w={{ base: '100%', md: '160px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleCancel}
            _hover={{ bg: 'surface.controlHover' }}>
            Back
          </Button>

          <Button
            buttonName="save-edit-model"
            bg="icon.green"
            color="white"
            borderRadius="lg"
            w={{ base: '100%', md: '200px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
            isDisabled={!areAllModelsValid || isSubmitting}
            _hover={{ opacity: 0.9 }}
            _disabled={{
              bg: 'icon.green',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}>
            Save Changes
          </Button>
        </Flex>
      </Box>
    </>
  );
};
